import React, { useEffect, useState } from 'react';
import './TodasNovedades.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const NOTICIAS_POR_PAGINA = 10;

const TodasNovedades = () => {
  const navigate = useNavigate();
  const [noticias, setNoticias] = useState([]);
  const [noticiasFiltradas, setNoticiasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [ordenarPor, setOrdenarPor] = useState('fecha_desc');
  const [paginaActual, setPaginaActual] = useState(1);

  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        setLoading(true);
        let nextUrl = `${process.env.REACT_APP_API_URL}/noticias/?page_size=50`;
        const noticiasAcumuladas = [];

        while (nextUrl) {
          const response = await axios.get(nextUrl);
          const data = response.data;

          if (Array.isArray(data)) {
            noticiasAcumuladas.push(...data);
            nextUrl = null;
          } else {
            noticiasAcumuladas.push(...(data.results || []));
            nextUrl = data.next;
          }
        }

        setNoticias(noticiasAcumuladas);
        setNoticiasFiltradas(noticiasAcumuladas);
        setError(null);
      } catch (err) {
        console.error('Error cargando noticias:', err);
        setError('Error al cargar las noticias');
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    let filtradas = [...noticias];

    if (busqueda.trim()) {
      const termino = busqueda.toLowerCase();
      filtradas = filtradas.filter((noticia) =>
        noticia.titulo.toLowerCase().includes(termino) ||
        noticia.contenido.toLowerCase().includes(termino)
      );
    }

    if (fechaDesde) {
      filtradas = filtradas.filter(
        (noticia) => new Date(noticia.fecha_publicacion) >= new Date(fechaDesde)
      );
    }

    if (fechaHasta) {
      filtradas = filtradas.filter(
        (noticia) => new Date(noticia.fecha_publicacion) <= new Date(fechaHasta)
      );
    }

    filtradas.sort((a, b) => {
      switch (ordenarPor) {
        case 'fecha_desc':
          return new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion);
        case 'fecha_asc':
          return new Date(a.fecha_publicacion) - new Date(b.fecha_publicacion);
        case 'titulo_asc':
          return a.titulo.localeCompare(b.titulo);
        case 'titulo_desc':
          return b.titulo.localeCompare(a.titulo);
        default:
          return 0;
      }
    });

    setNoticiasFiltradas(filtradas);
    setPaginaActual(1);
  }, [noticias, busqueda, fechaDesde, fechaHasta, ordenarPor]);

  const handleLeerMas = (id) => {
    navigate(`/novedades/${id}`);
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setFechaDesde('');
    setFechaHasta('');
    setOrdenarPor('fecha_desc');
  };

  const formatFecha = (fechaString) => {
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const extractText = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html || '';
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const totalPaginas = Math.ceil(noticiasFiltradas.length / NOTICIAS_POR_PAGINA);
  const indiceInicial = (paginaActual - 1) * NOTICIAS_POR_PAGINA;
  const noticiasPaginadas = noticiasFiltradas.slice(
    indiceInicial,
    indiceInicial + NOTICIAS_POR_PAGINA
  );

  if (loading) {
    return (
      <div className="todas-novedades-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando noticias...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="todas-novedades-container">
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="todas-novedades-container">
      <div className="header-pagina">
        <h1>Todas las Novedades</h1>
        <p>Explora todas las noticias y novedades de la DPA</p>
      </div>

      <div className="filtros-panel">
        <div className="filtros-header">
          <h3>Filtros</h3>
          <button className="limpiar-filtros-btn" onClick={limpiarFiltros}>
            Limpiar
          </button>
        </div>

        <div className="filtros-fila">
          <div className="filtro-grupo filtro-busqueda">
            <label>Buscar</label>
            <input
              type="text"
              placeholder="Buscar en titulos y contenido..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="busqueda-input"
            />
          </div>

          <div className="filtro-grupo">
            <label>Fecha desde</label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="fecha-input"
            />
          </div>

          <div className="filtro-grupo">
            <label>Fecha hasta</label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="fecha-input"
            />
          </div>

          <div className="filtro-grupo">
            <label>Ordenar por</label>
            <select
              value={ordenarPor}
              onChange={(e) => setOrdenarPor(e.target.value)}
              className="ordenar-select"
            >
              <option value="fecha_desc">Mas recientes primero</option>
              <option value="fecha_asc">Mas antiguos primero</option>
              <option value="titulo_asc">Titulo A-Z</option>
              <option value="titulo_desc">Titulo Z-A</option>
            </select>
          </div>
        </div>

        <div className="estadisticas">
          <p>
            Mostrando {noticiasPaginadas.length} de {noticiasFiltradas.length} noticias
            {noticias.length !== noticiasFiltradas.length ? ` filtradas de ${noticias.length}` : ' totales'}
          </p>
        </div>
      </div>

      <div className="contenido-principal">
        {noticiasFiltradas.length === 0 ? (
          <div className="sin-resultados">
            <div className="sin-resultados-icon">?</div>
            <h3>No se encontraron noticias</h3>
            <p>Intenta ajustar los filtros de busqueda</p>
            <button className="limpiar-filtros-btn" onClick={limpiarFiltros}>
              Limpiar filtros
            </button>
          </div>
        ) : (
          <>
            <div className="noticias-grid">
              {noticiasPaginadas.map((noticia) => (
                <div key={noticia.id} className="noticia-card">
                  <div className="noticia-imagen">
                    <img
                      src={
                        noticia.imagen_principal_thumbnail ||
                        noticia.imagen_principal_url ||
                        '/placeholder-image.jpg'
                      }
                      alt={noticia.titulo}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                  </div>
                  <div className="noticia-contenido">
                    <h3 className="noticia-titulo">{noticia.titulo}</h3>
                    <p className="noticia-fecha">{formatFecha(noticia.fecha_publicacion)}</p>
                    <p className="noticia-descripcion">
                      {(() => {
                        const textoLimpio = extractText(noticia.contenido);
                        return textoLimpio.length > 180
                          ? `${textoLimpio.substring(0, 180)}...`
                          : textoLimpio;
                      })()}
                    </p>
                    <button
                      className="leer-mas-btn"
                      onClick={() => handleLeerMas(noticia.id)}
                    >
                      Leer mas
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPaginas > 1 && (
              <div className="paginacion">
                <button
                  className="paginacion-btn"
                  onClick={() => setPaginaActual((prev) => Math.max(prev - 1, 1))}
                  disabled={paginaActual === 1}
                >
                  Anterior
                </button>

                <span className="paginacion-info">
                  Pagina {paginaActual} de {totalPaginas}
                </span>

                <button
                  className="paginacion-btn"
                  onClick={() => setPaginaActual((prev) => Math.min(prev + 1, totalPaginas))}
                  disabled={paginaActual === totalPaginas}
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TodasNovedades;
